import { useState, useEffect } from "react";
import { Users, RefreshCw, Wifi, WifiOff, Ban, Copy, Check, History } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Section } from "./Section";

const STATUS = {
  active: { label: "Aktywna", cls: "bg-emerald-900/40 text-emerald-300 border-emerald-800" },
  expired: { label: "Wygasła", cls: "bg-red-900/40 text-red-300 border-red-800" },
  revoked: { label: "Odwołana", cls: "bg-slate-800 text-slate-400 border-slate-700 line-through" },
};

function fmtDate(s) {
  return s ? String(s).slice(0, 10) : "—";
}
function fmtWhen(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" }); }
  catch { return String(s).slice(0, 16).replace("T", " "); }
}
function contactStale(it) {
  if (!it.phone_interval || !it.last_seen_at) return !!it.phone_interval && !it.last_seen_at;
  const days = (Date.now() - new Date(it.last_seen_at).getTime()) / 86400000;
  return days > it.phone_interval;
}

export function LicenseClients({ t, open, onToggle }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [renewed, setRenewed] = useState(null);
  const [copied, setCopied] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [tlOpen, setTlOpen] = useState(null);
  const [tl, setTl] = useState({});

  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get("/license/clients"); setRows(Array.isArray(data) ? data : []); }
    catch { /* ignore */ }
    finally { setBusy(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const toggleTimeline = async (it) => {
    if (tlOpen === it.id) { setTlOpen(null); return; }
    setTlOpen(it.id);
    if (!tl[it.id]) {
      try { const { data } = await api.get(`/license/clients/${it.id}/timeline`); setTl((m) => ({ ...m, [it.id]: data.events || [] })); }
      catch { /* ignore */ }
    }
  };

  const renew = async (it) => {
    const d = window.prompt(t("Nowa data ważności (RRRR-MM-DD), puste = bezterminowa:"), it.expires || "");
    if (d === null) return;
    try {
      const { data } = await api.post(`/license/${it.id}/renew`, { expires: (d || "").trim() });
      setRenewed({ company: it.company, key: data.key });
      toast.success(t("Wygenerowano odnowiony klucz — skopiuj i wyślij klientowi"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("Błąd odnowienia")); }
  };

  const revoke = async (it) => {
    if (!window.confirm(t("Oznaczyć licencję jako wygaszoną/odwołaną? Trafi na listę odwołań (CRL) i zablokuje instalację klienta przy kolejnym kontakcie."))) return;
    try {
      await api.post(`/license/${it.id}/revoke`);
      toast.success(t("Oznaczono jako odwołaną"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("Błąd")); }
  };

  const copyKey = async () => {
    try { await navigator.clipboard.writeText(renewed.key); setCopied(true); setTimeout(() => setCopied(false), 1800); toast.success(t("Skopiowano klucz")); }
    catch { toast.error(t("Nie udało się skopiować")); }
  };

  const visible = rows.filter((it) => {
    if (q && !(`${it.company} ${it.client_email}`.toLowerCase().includes(q.toLowerCase()))) return false;
    if (filter === "active") return it.status === "active";
    if (filter === "expiring") return it.status === "active" && typeof it.days_left === "number" && it.days_left <= 30;
    if (filter === "expired") return it.status === "expired";
    if (filter === "revoked") return it.status === "revoked";
    return true;
  });
  const FILTERS = [["all", "Wszystkie"], ["active", "Aktywne"], ["expiring", "Wygasające"], ["expired", "Wygasłe"], ["revoked", "Odwołane"]];

  return (
    <Section icon={Users} iconColor="text-cyan-400" title={t("Panel klientów (licencje)")}
      open={open} onToggle={onToggle} testid="settings-sec-licclients">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] text-slate-400">{t("Firmy z licencjami — status, kontakt phone-home i szybkie akcje.")}</span>
        <button type="button" onClick={load} disabled={busy} data-testid="licclients-refresh"
          className="inline-flex items-center gap-1 text-[11px] bg-slate-700 hover:bg-slate-600 rounded px-2 py-1 disabled:opacity-50">
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> {t("Odśwież")}
        </button>
      </div>

      {renewed && (
        <div className="mb-2 bg-slate-900/70 border border-emerald-800/50 rounded p-2.5" data-testid="licclients-renewed">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] font-semibold text-emerald-300">{t("Odnowiony klucz")} — {renewed.company}</span>
            <button type="button" onClick={copyKey} data-testid="licclients-renewed-copy"
              className="inline-flex items-center gap-1 text-[11px] text-cyan-400 hover:text-cyan-300">
              {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? t("Skopiowano") : t("Kopiuj")}
            </button>
          </div>
          <div className="font-mono text-[10px] text-slate-200 break-all bg-slate-950 rounded p-2 border border-slate-800">{renewed.key}</div>
          <p className="text-[10px] text-slate-500 mt-1">{t("Wyślij ten klucz klientowi — wkleja go w Ustawienia → Marka → Klucz licencyjny.")}</p>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="text-[11px] text-slate-500" data-testid="licclients-empty">{t("Brak wystawionych licencji.")}</div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Szukaj firmy lub e-maila…")}
              data-testid="licclients-search"
              className="flex-1 bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-[11px] text-white outline-none focus:ring-1 focus:ring-cyan-500" />
          </div>
          <div className="flex flex-wrap gap-1 mb-2">
            {FILTERS.map(([k, lbl]) => (
              <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`licclients-filter-${k}`}
                className={`text-[10px] px-2 py-1 rounded border ${filter === k ? "bg-cyan-600 text-white border-cyan-600" : "bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700"}`}>
                {t(lbl)}
              </button>
            ))}
          </div>
          <div className="space-y-1.5 max-h-96 overflow-auto pr-1" data-testid="licclients-list">
          {visible.map((it) => {
            const st = STATUS[it.status] || STATUS.active;
            const warn = it.status === "active" && typeof it.days_left === "number" && it.days_left <= 30;
            const stale = contactStale(it);
            return (
              <div key={it.id} data-testid={`licclients-row-${it.id}`}
                className="rounded px-2.5 py-2 border bg-slate-900/60 border-slate-800">
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-200 truncate flex items-center gap-1.5">
                      {it.company}
                      {it.hw ? <span className="text-[9px] text-cyan-500">🔒</span> : null}
                    </div>
                    <div className="text-[10px] text-slate-500 truncate">{it.client_email || t("brak e-maila")}</div>
                  </div>
                  <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border ${st.cls}`} data-testid={`licclients-status-${it.id}`}>
                    {t(st.label)}
                  </span>
                  <div className="shrink-0 flex items-center gap-2 pl-1">
                    <button type="button" onClick={() => toggleTimeline(it)} data-testid={`licclients-timeline-${it.id}`}
                      title={t("Historia odnowień")} className="inline-flex items-center text-slate-400 hover:text-cyan-300">
                      <History size={13} />
                    </button>
                    <button type="button" onClick={() => renew(it)} data-testid={`licclients-renew-${it.id}`}
                      title={t("Zatwierdź odnowienie (po opłacie) — wystawia nowy klucz")}
                      className="inline-flex items-center text-emerald-400 hover:text-emerald-300">
                      <RefreshCw size={13} />
                    </button>
                    {it.status !== "revoked" && (
                      <button type="button" onClick={() => revoke(it)} data-testid={`licclients-revoke-${it.id}`}
                        title={t("Wygaś / odwołaj licencję")}
                        className="inline-flex items-center text-red-400 hover:text-red-300">
                        <Ban size={13} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-1 flex items-center justify-between text-[10px] text-slate-400">
                  <span>
                    {t("do")}: <span className="text-slate-300">{it.expires ? fmtDate(it.expires) : t("bezterminowa")}</span>
                    {typeof it.days_left === "number" && (
                      <span className={warn ? "text-amber-400 ml-1" : "text-slate-500 ml-1"}>
                        ({it.days_left >= 0 ? `${it.days_left} ${t("dni")}` : t("po terminie")})
                      </span>
                    )}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    {it.phone_interval ? (
                      stale
                        ? <><WifiOff size={11} className="text-red-400" /> <span className="text-red-400">{fmtWhen(it.last_seen_at)}</span></>
                        : <><Wifi size={11} className="text-emerald-400" /> <span>{fmtWhen(it.last_seen_at)}</span></>
                    ) : (
                      <span className="text-slate-600">{t("offline")}</span>
                    )}
                  </span>
                </div>
                {(it.last_version || it.renewal_requested_at) && (
                  <div className="mt-1 flex items-center justify-between text-[9px]">
                    <span className="text-slate-500">{it.last_version ? <>v{it.last_version}</> : "—"}</span>
                    {it.renewal_requested_at && <span className="text-amber-400" data-testid={`licclients-pending-${it.id}`}>⏳ {t("prośba o odnowienie — zatwierdź po opłacie")}</span>}
                  </div>
                )}
                {tlOpen === it.id && (
                  <div className="mt-2 border-t border-slate-800 pt-2 space-y-1" data-testid={`licclients-timeline-list-${it.id}`}>
                    <div className="text-[10px] font-semibold text-slate-400 mb-0.5">{t("Historia odnowień")}</div>
                    {(tl[it.id] || []).length === 0 ? (
                      <div className="text-[10px] text-slate-600">{t("Ładowanie…")}</div>
                    ) : (tl[it.id].map((ev, i) => (
                      <div key={i} className="flex items-center justify-between text-[10px] text-slate-400">
                        <span>{(ev.created_at || "").slice(0, 10)}</span>
                        <span className="text-slate-300">{t("do")} {ev.expires || t("bezterminowa")}</span>
                        <span className={ev.revoked ? "text-red-400" : ev.renewed ? "text-slate-500" : "text-emerald-400"}>
                          {ev.revoked ? t("odwołana") : ev.renewed ? t("zastąpiona") : (ev.renewed_from ? t("odnowienie") : t("wystawienie"))}
                        </span>
                      </div>
                    )))}
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </>
      )}
    </Section>
  );
}
